import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormInputFile } from '@/components/forms/form-input-file';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  createStudentUsersByCsv,
  getDroppedStudents,
  getSchools,
  getStudents,
  getStudentsOfSchool,
  getStudentsWithMultipleSchedules,
  getUsers,
} from '@/lib/api-calls';
import { csvFormSchema, parseCsvAndUpload } from '@/lib/csv-utils';
import { pluralize } from '@/lib/string-utils';
import { PostStudentUsersCSVFormKeys, postStudentUserCSVRow, postStudentUsersCSVBody } from '@airreading/common';
import { allPagesModifier } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { z } from 'zod';

interface CreateStudentsByCsvProps {
  trigger: ReactNode;
  onSuccess?: () => void;
  schoolId?: string;
  from?: 'users' | 'students' | 'school' | 'dropped' | 'multiple-schedule';
}
const EMPTY_ERRORS: string[] = [];
const formSchema = postStudentUsersCSVBody.merge(csvFormSchema);

export const CreateStudentsByCsv = ({ trigger, onSuccess, schoolId, from }: CreateStudentsByCsvProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);
  const { mutateAsync } = useMutation({
    mutationFn: createStudentUsersByCsv,
  });

  const { data: allSchoolsResponse, isPending: isLoadingSchools } = useQuery(getSchools(allPagesModifier));

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { csvFile, schoolId } = formData;

    const result = await parseCsvAndUpload({
      file: csvFile[0],
      rowSchema: postStudentUserCSVRow,
      onUpload: async (file) => {
        const uploadForm = new FormData();
        uploadForm.append(PostStudentUsersCSVFormKeys.studentsCSV, file);
        uploadForm.append(PostStudentUsersCSVFormKeys.schoolId, schoolId);
        return mutateAsync(uploadForm);
      },
      onToast: toast,
      onSetErrors: setErrorMessages,
    });

    const { successRowsCount, hasErrors } = result;

    if (successRowsCount > 0) {
      toast({ title: `${successRowsCount} new ${pluralize(successRowsCount, 'student', 'students')} created` });
      onSuccess?.();

      switch (from) {
        case 'school':
          if (schoolId) {
            queryClient.invalidateQueries(getStudentsOfSchool(schoolId));
          }
          break;
        case 'students':
          queryClient.invalidateQueries(getStudents());
          break;
        case 'users':
          queryClient.invalidateQueries(getUsers());
          break;
        case 'dropped':
          queryClient.invalidateQueries(getDroppedStudents());
          break;
        case 'multiple-schedule':
          queryClient.invalidateQueries(getStudentsWithMultipleSchedules());
          break;
        default:
          queryClient.invalidateQueries(getStudents());
          break;
      }
    }

    if (!hasErrors) {
      closeModal();
    }

    return successRowsCount;
  };

  return (
    <FormModal
      modalTitle="Create students by CSV"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      onClose={() => setErrorMessages(EMPTY_ERRORS)}
      defaultValues={{
        ...(schoolId && { schoolId }),
      }}
    >
      {({ form }) => {
        return (
          <>
            <div className="grid grid-cols-1 gap-2 py-4">
              <FormSingleSelect
                form={form}
                name="schoolId"
                label="School"
                options={(allSchoolsResponse?.schools || []).map((school) => school.id)}
                renderOption={(id) =>
                  (allSchoolsResponse?.schools || []).find((school) => school.id === id)?.name || ''
                }
                loading={isLoadingSchools}
                triggerProps={{ disabled: !!schoolId }}
              />

              <FormInputFile
                form={form}
                name="csvFile"
                label="Students CSV"
                inputProps={{
                  accept: '.csv',
                  className: 'w-full',
                }}
              />
              <span>
                See{' '}
                <a
                  href="https://docs.google.com/spreadsheets/d/1xJovBqkai1WECe3EL-xNfjow83txRBrNxktTE1Jskwo/edit?usp=sharing"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-600 hover:underline"
                >
                  template (to update)
                </a>{' '}
                for CSV format. Export the sheet as csv file
              </span>
            </div>
            {errorMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error while parsing CSV</AlertTitle>
                  <AlertDescription className="max-h-[240px] overflow-y-auto">
                    {errorMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
};
